import { MapPin } from "lucide-react";

import { BreathingRings } from "../../components/breathing-rings";
import { CoachPhoto } from "../../components/coach-photo";
import { DotRing, litDots } from "../../components/dot-ring";
import { GoLink } from "../../components/go-link";
import { card, figure, inkButton, inkTag, label, primaryCard, tag } from "../../components/section";
import { SiteNav } from "../../components/site-head";
import { contactOf, useSiteConfig } from "../../hooks/use-site-config";
import type { PublicDirectory, PublicSpecialist } from "../../types";
import { coachPlaces, dayShare, openShare, placeList, soonestOpening } from "../../utils/directory";
import { openDayLabelIn, reachOutLabel, timeLabel } from "../../utils/labels";
import { SlotGateDialog } from "../open-calendar/slot-gate-dialog";
import { useSlotGate } from "../open-calendar/use-slot-gate";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** The rings fill their row: three coaches get three columns, not four with an empty seat. */
const ringColumns = (count: number) =>
  count >= 4 ? "md:grid-cols-4" : count === 3 ? "md:grid-cols-3" : count === 2 ? "md:grid-cols-2" : "md:grid-cols-1";

/** The order a team's professions are named in, and how the badge says each. */
const PROFESSION_ORDER = ["trainer", "physio", "dietician"] as const;
const PROFESSION_LABELS: Record<(typeof PROFESSION_ORDER)[number], () => string> = {
  trainer: () => m.hero_badge_trainer(),
  physio: () => m.hero_badge_physio(),
  dietician: () => m.hero_badge_dietician(),
};

/** Coaches the hero has room for. */
const VISIBLE_COACHES = 4;
/** Dots on the clock ring: one per 45 minutes of the day. */
const CLOCK_DOTS = 32;
const COACH_DOTS = 24;

/**
 * The solid card: the soonest time anyone can be booked, on a ring that reads like a clock face
 * (the lit dots run from midnight to the time). The button takes that exact time to sign-in.
 */
const NextOpen = ({
  directory,
  loading,
  onBook,
}: {
  directory: PublicDirectory | null;
  loading: boolean;
  onBook: (specialist: PublicSpecialist, startAt: string) => void;
}) => {
  const contact = contactOf(useSiteConfig());
  const next = directory ? soonestOpening(directory.specialists) : null;
  const shell = cn(primaryCard, "col-span-2 flex flex-col items-center text-center md:col-span-4 md:pt-9");

  if (loading) return <Skeleton className="col-span-2 min-h-[420px] rounded-[28px] md:col-span-4" />;

  if (!directory || !next) {
    return (
      <div className={cn(shell, "justify-center")}>
        <DotRing filled={0} total={CLOCK_DOTS} dot={2.6} tone="primary" className="mb-7 size-[200px]" />
        <p className="text-[22px] font-semibold tracking-[-0.02em]">{m.hero_next_empty_title()}</p>
        <p className="text-[var(--site-on-primary-soft)]">{reachOutLabel(contact)}</p>
        {contact ? (
          <a href={contact.href} className={cn(inkButton, "mt-6 h-[54px] w-full text-[15px]")}>
            {contact.label}
          </a>
        ) : null}
      </div>
    );
  }

  const { specialist, startAt } = next;
  const service = directory.services.find((candidate) => candidate.id === specialist.serviceId) ?? null;

  return (
    <div className={shell}>
      <span className={cn(inkTag, "self-center")}>{m.hero_next_tag()}</span>
      <DotRing
        filled={litDots(dayShare(startAt, specialist.timeZone), CLOCK_DOTS)}
        total={CLOCK_DOTS}
        dot={2.6}
        tone="primary"
        className="mt-2 mb-7 size-[230px]"
      >
        <div>
          <div className={cn(figure, "text-[68px]")}>{timeLabel(startAt, specialist.timeZone)}</div>
          <div className="mt-1.5 text-base text-[var(--site-on-primary-soft)]">
            {openDayLabelIn(startAt, specialist.timeZone)}
          </div>
        </div>
      </DotRing>
      <p className="text-[22px] font-semibold tracking-[-0.02em]">{service?.name ?? m.hero_next_session()}</p>
      <p className="mb-6 text-[var(--site-on-primary-soft)]">
        {service ? m.hero_next_with({ minutes: service.durationMinutes, name: specialist.name }) : specialist.name}
      </p>
      <button
        type="button"
        data-tour="hero-book"
        className={cn(inkButton, "mt-auto h-[54px] w-full text-[15px]")}
        onClick={() => onBook(specialist, startAt)}
      >
        {m.hero_next_book()}
      </button>
    </div>
  );
};

/** A bio as a line of plain text: Markdown links keep their words, marks are dropped. */
const plainText = (markdown: string) =>
  markdown
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#*_>`]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const openLabel = (open: number) =>
  open === 0 ? m.hero_coach_full() : open === 1 ? m.hero_coach_open_one() : m.hero_coach_open_other({ count: open });

/** One coach on a ring: the lit share is how much of their week is still open. */
const CoachRing = ({ specialist, where }: { specialist: PublicSpecialist; where: string }) => {
  const { open, share } = openShare(specialist);

  return (
    <a
      href="#times"
      className="group flex flex-col items-center gap-3 rounded-[20px] py-2 text-center outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <DotRing filled={litDots(share, COACH_DOTS)} total={COACH_DOTS} dot={3.4} className="size-[104px]">
        <CoachPhoto
          name={specialist.name}
          src={specialist.avatarUrl}
          className="size-[62px] rounded-full text-lg transition-transform duration-200 group-hover:scale-105 motion-reduce:transition-none"
        />
      </DotRing>
      <span className="grid gap-0.5">
        <b className="block text-xl font-medium tracking-[-0.02em]">{specialist.name}</b>
        <span className={label}>{openLabel(open)}</span>
        {where ? (
          <span className={cn(label, "inline-flex items-center justify-center gap-1")}>
            <MapPin aria-hidden className="size-3" />
            {where}
          </span>
        ) : null}
      </span>
    </a>
  );
};

/**
 * A coach with room to be introduced: the photo large on its ring, and beside it the name, what
 * they do and how open their week is. `size` is how much room: the whole card for a coach on
 * their own, half of it for one of two. The first lines of their bio come along when there is room.
 */
const CoachFeature = ({
  specialist,
  size,
  where,
}: {
  specialist: PublicSpecialist;
  size: "solo" | "pair";
  /** Where they work, said only when the business has more than one place. */
  where: string;
}) => {
  const { open, share } = openShare(specialist);
  const solo = size === "solo";

  return (
    <a
      href="#times"
      className={cn(
        "group flex min-w-0 flex-col items-start gap-5 rounded-[20px] outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-row sm:items-center",
        solo ? "sm:gap-9" : "sm:gap-6",
      )}
    >
      <DotRing
        filled={litDots(share, COACH_DOTS + 12)}
        total={COACH_DOTS + 12}
        dot={solo ? 4.2 : 3.6}
        className={cn("shrink-0", solo ? "size-[156px] md:size-[188px]" : "size-[128px]")}
      >
        <CoachPhoto
          name={specialist.name}
          src={specialist.avatarUrl}
          className={cn(
            "rounded-full transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transition-none",
            solo ? "size-[108px] text-3xl md:size-[132px] md:text-4xl" : "size-[86px] text-2xl",
          )}
        />
      </DotRing>
      <span className="grid min-w-0 gap-2">
        <b
          className={cn(
            "block font-medium tracking-[-0.035em] text-balance break-words",
            solo ? "text-[clamp(2rem,4vw,3rem)] leading-[1]" : "text-[28px] leading-[1.05]",
          )}
        >
          {specialist.name}
        </b>
        {specialist.title ? (
          <span className={cn("text-muted-foreground", solo ? "text-lg md:text-xl" : "text-base")}>
            {specialist.title}
          </span>
        ) : null}
        {solo && specialist.bio ? (
          <span className="line-clamp-2 max-w-[52ch] text-[15px] leading-6 text-muted-foreground/80">
            {plainText(specialist.bio)}
          </span>
        ) : null}
        <span className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-medium">
          <span className="inline-flex items-center gap-2">
            <span aria-hidden className={cn("size-2 rounded-full", open > 0 ? "bg-primary" : "bg-muted-foreground/50")} />
            {openLabel(open)}
          </span>
          {where ? (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <MapPin aria-hidden className="size-3.5" />
              {where}
            </span>
          ) : null}
        </span>
      </span>
    </a>
  );
};

/**
 * The coaches on the board, laid out by how many there are: one is introduced properly, two
 * share the card half each, three and more stand side by side on their rings.
 */
const CoachBoard = ({ directory }: { directory: PublicDirectory }) => {
  const shown = directory.specialists.slice(0, VISIBLE_COACHES);
  // With one place everyone works there, and saying so on every card is noise.
  const where = (specialist: PublicSpecialist) =>
    (directory.places?.length ?? 0) > 1
      ? placeList(coachPlaces(directory, specialist), (count) => m.hero_places_more({ count }))
      : "";
  if (shown.length === 1 && shown[0]) return <CoachFeature specialist={shown[0]} size="solo" where={where(shown[0])} />;
  if (shown.length === 2) {
    return (
      <div className="grid gap-8 md:grid-cols-2 md:gap-6">
        {shown.map((specialist) => (
          <CoachFeature key={specialist.id} specialist={specialist} size="pair" where={where(specialist)} />
        ))}
      </div>
    );
  }
  return (
    <div className={cn("grid grid-cols-2 gap-x-2 gap-y-5", ringColumns(shown.length))}>
      {shown.map((specialist) => (
        <CoachRing key={specialist.id} specialist={specialist} where={where(specialist)} />
      ))}
    </div>
  );
};

type StatProps = { title: string; value: string; note: string; accent?: boolean; className?: string };

const Stat = ({ title, value, note, accent, className }: StatProps) => (
  <div className={cn(card, "flex min-h-[180px] flex-col justify-between", className)}>
    <span className={label}>{title}</span>
    <div>
      <div className={cn(figure, "text-[52px]", accent && "text-[var(--site-ink)]")}>{value}</div>
      <span className={label}>{note}</span>
    </div>
  </div>
);

/** Desktop columns each of the trainer's numbers takes, out of what the coaches leave free. */
const statSpans = { 1: "md:col-span-4", 2: "md:col-span-2" } as const;
const statSpansAlone = { 1: "md:col-span-12", 2: "md:col-span-6" } as const;

/**
 * The home page's opening: the bar, then a board of cards. One headline and one button on the
 * left, the soonest open time on the solid card, the coaches with how open their week is, and
 * whichever of the trainer's own numbers they filled in. Everything about coaches and times is
 * live from the booking pack; a fact nobody entered is left off rather than shown empty.
 */
export const Hero = ({ directory, loading }: { directory: PublicDirectory | null; loading: boolean }) => {
  const site = useSiteConfig();
  const { open, gate } = useSlotGate(directory);
  const specialists = directory?.specialists ?? [];
  const { stats } = site;
  const shown = Math.min(specialists.length, VISIBLE_COACHES);
  const withCoaches = specialists.length > 0 || loading;
  const { brand } = site;
  // A trainer's page is theirs at first sight: their photo, their words, or at least their name
  // and what they do. The platform's slogan is only for the platform's own front page.
  const cover = brand?.coverUrl ?? null;
  const headline = brand ? brand.headline || brand.name : m.hero_title();
  const lede = brand ? brand.lede || specialists.find((specialist) => specialist.title)?.title || "" : m.hero_lede();
  // The bar already names the business; the chip says where, and only names it on platform pages.
  // What the business does, said in the badge: a visitor knows at a glance whether they came to the
  // right place. Where it is lives on the coach cards and the contact page, not in the headline.
  const professions = new Set(new Set((directory?.specialists ?? []).flatMap((specialist) => specialist.professions ?? [])));
  const badge = brand
    ? PROFESSION_ORDER.filter((profession) => professions.has(profession))
        .map((profession) => PROFESSION_LABELS[profession]())
        .join(" · ")
    : [directory?.business.name, site.address].filter(Boolean).join(" · ");
  const numbers = [
    stats.sessionsCoached
      ? {
          title: m.hero_stat_sessions(),
          value: stats.sessionsCoached,
          note: stats.since ? m.hero_stat_since({ year: stats.since }) : "",
        }
      : null,
    stats.rebookRate
      ? { title: m.hero_stat_rebook(), value: stats.rebookRate, note: m.hero_stat_rebook_note(), accent: true }
      : null,
  ].filter((stat) => stat !== null);
  // The numbers share what the coaches leave of the row, or the whole row when there are none.
  const statSpan = numbers.length > 0 ? (withCoaches ? statSpans : statSpansAlone)[numbers.length as 1 | 2] : "";

  return (
    <>
      <SiteNav />
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-12">
        <div
          className={cn(
            card,
            "col-span-2 flex min-h-[440px] flex-col justify-between md:col-span-8 md:min-h-[520px] md:p-11",
            cover && "text-white",
          )}
        >
          {cover ? (
            <>
              <img src={cover} alt="" className="absolute inset-0 -z-10 size-full object-cover" />
              {/* Dark where the words sit, so they read on any photo the trainer picks. */}
              <div
                aria-hidden
                className="absolute inset-0 -z-10"
                style={{
                  background:
                    "linear-gradient(180deg, rgb(0 0 0 / 0.4), rgb(0 0 0 / 0.05) 30%, rgb(0 0 0 / 0.8)), linear-gradient(90deg, rgb(0 0 0 / 0.5), transparent 75%)",
                }}
              />
            </>
          ) : (
            <BreathingRings className="-top-[200px] -right-[220px] size-[560px] opacity-50 md:-top-[120px] md:-right-[120px] md:opacity-90" />
          )}
          {badge ? (
            <span className={cn(tag, "max-w-full", cover && "bg-black/40 text-white backdrop-blur-md")}>
              <span className="truncate">{badge}</span>
            </span>
          ) : (
            <span />
          )}
          <div>
            <h1
              className={cn(
                "text-balance font-medium",
                brand
                  ? "max-w-[13ch] text-[clamp(2.75rem,6vw,5.75rem)] leading-[0.96] tracking-[-0.05em]"
                  : "max-w-[7.5ch] text-[clamp(3rem,7.2vw,7rem)] leading-[0.94] tracking-[-0.055em]",
              )}
            >
              {headline}
            </h1>
            {lede ? (
              <p className={cn("mt-[22px] max-w-[42ch] text-[17px]", cover ? "text-white/80" : "text-muted-foreground")}>
                {lede}
              </p>
            ) : null}
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <GoLink to="#times">{m.hero_cta()}</GoLink>
              {specialists.length > 0 ? (
                <div className="flex items-center gap-3">
                  <div className="flex shrink-0">
                    {specialists.slice(0, VISIBLE_COACHES).map((specialist) => (
                      <CoachPhoto
                        key={specialist.id}
                        name={specialist.name}
                        src={specialist.avatarUrl}
                        className={cn(
                          "-ml-2.5 size-11 rounded-2xl border-[3px] text-xs first:ml-0",
                          cover ? "border-white/25" : "border-card",
                        )}
                      />
                    ))}
                  </div>
                  <div>
                    <b className="block font-semibold">
                      {specialists.length === 1
                        ? m.hero_coaches_one()
                        : m.hero_coaches_other({ count: specialists.length })}
                    </b>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <NextOpen
          directory={directory}
          loading={loading}
          onBook={(specialist, startAt) =>
            open({ specialistId: specialist.id, serviceId: specialist.serviceId, startAt })
          }
        />

        {specialists.length > 0 ? (
          <div
            className={cn(
              card,
              "col-span-2 flex flex-col justify-center",
              numbers.length > 0 ? "md:col-span-8" : "md:col-span-12",
              shown <= 2 && "md:p-10",
            )}
          >
            {directory ? <CoachBoard directory={directory} /> : null}
          </div>
        ) : loading ? (
          <Skeleton
            className={cn("col-span-2 min-h-[180px] rounded-[28px]", numbers.length > 0 ? "md:col-span-8" : "md:col-span-12")}
          />
        ) : null}
        {numbers.map((stat) => (
          <Stat key={stat.title} {...stat} className={statSpan} />
        ))}
      </div>
      <SlotGateDialog {...gate} />
    </>
  );
};
